import { Alert, AlertIcon, Container, Flex, Image, Link, Stack, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { IoMdInformationCircleOutline } from 'react-icons/io';

import { FinishLineGuy, StandUpGuy } from '../../assets/Images';
import { useCheckWarnings, useTempInfo } from '../../hooks';
import { CookieKeys, clearCookie } from '../../hooks/useCookie';
import getSearchPhoneNumbers from '../../utils/getSearchPhoneNumbers';
import { sellsViaMarketplace } from '../../utils/tempInfo';
import { ResidenceFormValues } from '../LoanApp/utils';
import DashboardStateCard from '../dashboard/dashboardStates/DashboardStateCard';
import FaqsWrapper from './FaqsWrapper';
import InTheMeantime from './InTheMeantime';

const DynamicNicelyDone = () => {
  const { info } = useTempInfo();
  const [, setChecksRan] = useState<boolean>(false);
  const tempInfoData = info?.data;
  const passedPrequal = !!tempInfoData?.prequalification_success;
  const wantsToSell = sellsViaMarketplace(tempInfoData);

  const phoneNumber = getSearchPhoneNumbers();

  const { warnings, checkWarnings, anyWarning } = useCheckWarnings();
  const wipeReferral = () => {
    clearCookie(CookieKeys.REFERRAL_CODE);
    clearCookie(CookieKeys.REFERRAL_ID);
    clearCookie(CookieKeys.REFERRER_FN);
  };
  useEffect(() => {
    const runChecks = async () => {
      if (!tempInfoData) {
        setChecksRan(true);
        return;
      }
      await checkWarnings({
        address_line: tempInfoData.address_line,
        address_line_2: tempInfoData?.address_line_2,
        city: tempInfoData?.city,
        state: tempInfoData?.state,
        zip: tempInfoData?.zip,
        moved_states: tempInfoData?.moved_states,
      } as Partial<ResidenceFormValues>);
      setChecksRan(true);
    };

    runChecks();
  }, [tempInfoData, checkWarnings]);
  useEffect(() => {
    wipeReferral();
  }, []);
  const COPY = {
    passedPrequal: {
      header: 'Great news!',
      subheader: 'We found you an approval for your lease buyout.',
      body: (
        <Text>
          We’ll be calling soon to walk you through your next steps—get ready to make it official.
          In the meantime, if you have any questions, give us a call at{' '}
          <Link display="inline-block" href={`tel:+1-${phoneNumber}`}>
            {phoneNumber}
          </Link>
          .
        </Text>
      ),
      image: <Image src={StandUpGuy} />,
    },
    failedPrequal: {
      header: wantsToSell ? 'We’ve got it!' : 'We’re so close!',
      subheader: wantsToSell
        ? 'Our team is reviewing your information.'
        : 'Your lease buyout journey is underway.',
      body: (
        <Text>
          {wantsToSell ? (
            <>We’ll reach out soon to discuss next steps. If you’d like to connect sooner, call </>
          ) : (
            <>
              Every buyout is different—we’re reviewing your options and will reach out soon with
              next steps. Want to talk sooner? Give us a call at{' '}
            </>
          )}
          <Link display="inline-block" href={`tel:+1-${phoneNumber}`}>
            {phoneNumber}
          </Link>
          .
        </Text>
      ),
      image: <Image src={FinishLineGuy} />,
    },
  };

  return (
    <Container maxW="container.lg" mt="20px">
      <DashboardStateCard
        {...(passedPrequal ? COPY.passedPrequal : COPY.failedPrequal)}
        hideDocs
        hideAdvisors
      />
      {anyWarning && (
        <Stack spacing={2} mt={6}>
          {warnings.directPay && (
            <Alert
              status="info"
              rounded="md"
              bg="blue.50"
              borderColor="lightSkyBlue"
              borderWidth="1px"
            >
              <AlertIcon as={IoMdInformationCircleOutline} />
              <Text>
                Your lienholder and/or state requires you <b>visit the dealership</b> in person to
                complete your lease buyout. We’ll prepare everything—you just need to make the final
                visit.
              </Text>
            </Alert>
          )}
          {warnings.doubleTax && (
            <Alert
              status="info"
              rounded="md"
              bg="blue.50"
              borderColor="lightSkyBlue"
              borderWidth="1px"
            >
              <AlertIcon as={IoMdInformationCircleOutline} />
              <Text>
                Only the person on the lease can complete this buyout. If someone else is trying to
                complete the purchase, the payoff may change.
              </Text>
            </Alert>
          )}
          {warnings.requireWalkIn && (
            <Alert
              status="info"
              rounded="md"
              bg="blue.50"
              borderColor="lightSkyBlue"
              borderWidth="1px"
            >
              <AlertIcon as={IoMdInformationCircleOutline} />
              <Text>
                When it comes to titling your vehicle, your state requires that you complete the
                process in person. We’ll prepare everything—you just need to take it in.
              </Text>
            </Alert>
          )}
        </Stack>
      )}
      <Flex flexDir="column" alignItems="center">
        <InTheMeantime w="fit-content" mt={8} />
        <FaqsWrapper />
      </Flex>
    </Container>
  );
};

export default DynamicNicelyDone;
